// src/components/admin/staff/AdminStaffContent.jsx
import { useState, useEffect, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Search, Plus, Users, Loader2 } from "lucide-react";
import StaffList from "./List";
import StaffModal from "./AddEditModal";
import useAxiosAdmin from "../lib/axiosAdmin";
import {
  setAllDataOfAdminStaff,
  setFilteredAdminStaff,
} from "../../../redux/action/admin/staff/action";
import { SET_SHOULD_REFRESH_ADMIN_STAFF } from "../../../redux/action/admin/staff/type";

const AdminStaffContent = () => {
  const axiosAdmin = useAxiosAdmin();
  const dispatch = useDispatch();

  const { staff, filtered, total, loaded } = useSelector(
    (state) => state.adminStaff
  );

  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [editStaff, setEditStaff] = useState(null);
  const { shouldRefresh } = useSelector(state => state.adminStaff);

  console.log(staff);

  const fetchStaff = async () => {
    try {
      setLoading(true);

      const res = await axiosAdmin.get("/api/admin/staff", {
        params: {
          limit: 500,
        },
      });

      dispatch(
        setAllDataOfAdminStaff({
          page: res.data.page || 0,
          total: res.data.total || 0,
          pages: res.data.pages || 0,
          staff: res.data.staff || [],
        })
      );
    } catch (err) {
      console.error("خطا در دریافت کارکنان:", err);
      dispatch(
        setAllDataOfAdminStaff({
          page: 0,
          total: 0,
          pages: 0,
          staff: [],
        })
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    
    // 🔥 اگر shouldRefresh فعال بود، حتماً رفرش کن
    if (shouldRefresh) {

        fetchStaff();
        
        dispatch({
            type: SET_SHOULD_REFRESH_ADMIN_STAFF,
            payload: false
        });

        return;
    }

    // 🔥 اگر داده‌ها قبلاً لود شده بودن، نیازی به fetch نیست
    if (staff.length > 0) {
        setLoading(false);
        return;
    }

}, [shouldRefresh]);

  // فقط یک بار در اولین بارگذاری داده‌ها را دریافت می‌کند
  useEffect(() => {
    if (!loaded) {
      fetchStaff();
    }
  }, [loaded]);

  // فیلتر کردن محلی بر اساس جستجو
  useEffect(() => {
    if (!search.trim()) {
      dispatch(setFilteredAdminStaff(staff));
      return;
    }

    const timer = setTimeout(() => {
      const searchLower = search.toLowerCase().trim();
      const filtered = staff.filter((item) => {
        const displayName =
          item.information?.displayName?.toLowerCase() || "";
        const firstName = item.information?.firstName?.toLowerCase() || "";
        const lastName = item.information?.lastName?.toLowerCase() || "";
        const phone = item.information?.phone || "";

        return (
          displayName.includes(searchLower) ||
          firstName.includes(searchLower) ||
          lastName.includes(searchLower) ||
          phone.includes(searchLower)
        );
      });

      dispatch(setFilteredAdminStaff(filtered));
    }, 300);

    return () => clearTimeout(timer);
  }, [search, staff, dispatch]);

  const displayedTotal = search ? filtered.length : total;

  return (
    <div className="bg-gradient-to-br from-slate-50 via-purple-50/30 to-slate-50 p-0 md:p-8">
      
      <div className="max-w-7xl mx-auto">
       
        {/* Header Section */}
        <div className="bg-white rounded-2xl shadow-lg border border-purple-100/50 p-5 md:p-6 mb-6">
          
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            {/* Title & Stats */}
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 rounded-lg bg-gradient-to-br from-purple-500 to-purple-600 flex items-center justify-center shadow-md shadow-purple-500/25">
                <Users className="w-5 h-5 text-white" />
              </div>
              <div>
                <h1 className="text-xl font-doran md:text-xl font-bold text-slate-800">
                  مدیریت کارکنان سالن
                </h1>
                <p className="text-xs text-slate-500 mt-0.5">
                  {loading ? (
                    <span className="flex items-center gap-1.5">
                      <Loader2 className="w-3 h-3 animate-spin" />
                      در حال بارگذاری...
                    </span>
                  ) : (
                    <span>
                      {displayedTotal} کارمند {search && `(نتایج جستجو)`}
                    </span>
                  )}
                </p>
              </div>
            </div>

            {/* Add Button */}
            <button
              onClick={() => {
                setEditStaff(null);
                setOpenModal(true);
              }}
              className="group relative px-5 py-2.5 bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-700 hover:to-purple-800 text-white text-sm rounded-lg shadow-md shadow-purple-500/25 hover:shadow-lg hover:shadow-purple-500/30 transition-all duration-300 flex items-center justify-center gap-2 font-medium"
            >
              <Plus className="w-4 h-4 group-hover:rotate-90 transition-transform duration-300" />
              <span>افزودن کارکن جدید</span>
            </button>
          </div>

          {/* Search Bar */}
          <div className="mt-5 relative">
            <div className="absolute right-3.5 top-1/2 -translate-y-1/2 text-slate-400">
              <Search className="w-4 h-4" />
            </div>
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="جستجو بر اساس نام، نام خانوادگی، نام نمایشی یا شماره تلفن..."
              className="w-full pr-11 pl-4 py-3 text-sm bg-slate-50 border border-slate-200 rounded-lg text-slate-700 placeholder:text-slate-400 focus:outline-none focus:border-purple-400 focus:bg-white transition-all duration-300"
            />
            {search && (
              <button
                onClick={() => setSearch("")}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
              >
                <span className="text-lg">×</span>
              </button>
            )}
          </div>
        
        </div>

        {/* Staff List */}
        <div className="bg-white rounded-2xl shadow-lg border border-purple-100/50 overflow-hidden">
          <StaffList
            staff={filtered}
            loading={loading}
            onDelete={fetchStaff}
            onEdit={(item) => {
              setEditStaff(item);
              setOpenModal(true);
            }}
          />
        </div>

      </div>

      {/* Modal */}
      {openModal && (
        <StaffModal
          onClose={() => setOpenModal(false)}
          onSuccess={fetchStaff}
          editStaff={editStaff}
        />
      )}
    </div>
  );
};

export default AdminStaffContent;